import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../firebase/interfaces';
import {
  PharmacyInformationInput,
  useGetFhirPatientbyCodexIdQuery,
  useGetFhirPatientbyEmailQuery,
} from 'graphql/generated/remote-schema-hasura';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useMemo, useState } from 'react';
import { ProfileData } from 'app/my-account/interfaces/profile.interfaces';

const useProfileDataLoader = () => {
  const { user: loggedUser } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);

  const getMfaParts = (mfaPhoneNumber?: string) => {
    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        mfaPhoneNumber || '',
      );
      return {
        mfaPhone: parsedPhoneNumber?.nationalNumber ?? '',
        mfaCountry: parsedPhoneNumber?.countryCallingCode ?? '',
      };
    } catch (error) {
      console.warn('Unable to parse phone number');
      return {
        mfaPhone: '',
        mfaCountry: '',
      };
    }
  };

  const { loading: fhirPatientDataLoading, refetch: refetchProfile } =
    useGetFhirPatientbyCodexIdQuery({
      context: {
        ignoreGlobalErrors: true,
      },
      skip: !loggedUser?.uuid || loggedUser?.role !== Roles.PATIENT,
      fetchPolicy: 'network-only',
      onCompleted(data) {
        if (
          loggedUser?.role === Roles.PATIENT &&
          data?.getFHIRPatientbyCodexID.users.length > 0
        ) {
          const user = data?.getFHIRPatientbyCodexID.users[0];
          setProfileData({
            firstname: user?.SENSITIVE_firstname || '',
            lastname: user?.SENSITIVE_lastname || '',
            email: user?.SENSITIVE_email || '',
            gender: user?.SENSITIVE_gender || '',
            phone: user?.SENSITIVE_phone || '',
            dob: user?.SENSITIVE_dob || '',
            selfIdentifyGender: user?.SENSITIVE_self_identity_gender || '',
            genderDescribe: user?.SENSITIVE_gender_describe || '',
            is_state_dob_valid: user?.is_state_dob_valid || false,
            autonomyStatus: user?.autonomy_determination_status,
            evidenceTypes: user?.minor_autonomy_evidence ?? [],
            userMeasurements: {
              weight: user.SENSITIVE_user_measurements?.weight
                ? String(user?.SENSITIVE_user_measurements?.weight)
                : '',
              heightFt: user.SENSITIVE_user_measurements?.heightFt
                ? String(user?.SENSITIVE_user_measurements?.heightFt)
                : '',
              heightIn: user.SENSITIVE_user_measurements?.heightIn
                ? String(user?.SENSITIVE_user_measurements?.heightIn)
                : '',
              hips: user.SENSITIVE_user_measurements?.hips
                ? String(user?.SENSITIVE_user_measurements?.hips)
                : '',
              waist: user.SENSITIVE_user_measurements?.waist
                ? String(user?.SENSITIVE_user_measurements?.waist)
                : '',
            },
            etnicity: user?.SENSITIVE_etnicity || [],
            country: user?.SENSITIVE_country || '',
            addressInformation: {
              addressLine1: user?.SENSITIVE_address_information?.address1 || '',
              addressLine2: user?.SENSITIVE_address_information?.address2 || '',
              country: user?.SENSITIVE_address_information?.country || '',
              city: user.SENSITIVE_address_information?.city || '',
              state:
                user.SENSITIVE_address_information?.state ||
                loggedUser?.state ||
                '',
              zipCode: user.SENSITIVE_address_information?.zipCode || '',
              phone: user.SENSITIVE_address_information?.phone || '',
              phoneCountry:
                user.SENSITIVE_address_information?.phoneCountry || '',
            },
            signInProvider: loggedUser?.signInProvider || null,
            pharmacyList:
              user.SENSITIVE_pharmacies as PharmacyInformationInput[],
            ...getMfaParts(
              user?.SENSITIVE_country && user?.SENSITIVE_phone
                ? `+${user?.SENSITIVE_country}${user?.SENSITIVE_phone}`
                : '',
            ),
          });
        }
      },
    });

  const {
    data: fhirData,
    loading: fhirDataLoading,
    refetch: refetchFHIR,
  } = useGetFhirPatientbyEmailQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    skip: loggedUser?.role !== Roles.PATIENT,
    fetchPolicy: 'network-only',
    variables: { email: loggedUser?.email as string },
  });

  const fhirUserData = useMemo(() => {
    const user = fhirData?.getFHIRPatientbyEmail.users?.[0];
    return {
      firstname: user?.SENSITIVE_firstname || '',
      lastname: user?.SENSITIVE_lastname || '',
      email: user?.SENSITIVE_email || '',
      gender: user?.SENSITIVE_gender || '',
      phone: user?.SENSITIVE_phone || '',
      dob: user?.SENSITIVE_dob || '',
      selfIdentifyGender: user?.SENSITIVE_self_identity_gender || '',
      genderDescribe: user?.SENSITIVE_self_identity_gender || '',
      userMeasurements: {
        weight: user?.SENSITIVE_user_measurements?.weight
          ? String(user?.SENSITIVE_user_measurements?.weight)
          : '',
        heightFt: user?.SENSITIVE_user_measurements?.heightFt
          ? String(user?.SENSITIVE_user_measurements?.heightFt)
          : '',
        heightIn: user?.SENSITIVE_user_measurements?.heightIn
          ? String(user?.SENSITIVE_user_measurements?.heightIn)
          : '',
        hips: user?.SENSITIVE_user_measurements?.hips
          ? String(user?.SENSITIVE_user_measurements?.hips)
          : '',
        waist: user?.SENSITIVE_user_measurements?.waist
          ? String(user?.SENSITIVE_user_measurements?.waist)
          : '',
      },
      etnicity: user?.SENSITIVE_etnicity || [],
      country: user?.SENSITIVE_country || '',
      addressInformation: {
        addressLine1: user?.SENSITIVE_address_information?.address1 || '',
        addressLine2: user?.SENSITIVE_address_information?.address2 || '',
        country: user?.SENSITIVE_address_information?.country || '',
        city: user?.SENSITIVE_address_information?.city || '',
        state: user?.SENSITIVE_address_information?.state || '',
        zipCode: user?.SENSITIVE_address_information?.zipCode || '',
        phone: user?.SENSITIVE_address_information?.phone || '',
        phoneCountry: user?.SENSITIVE_address_information?.phoneCountry || '',
      },
      signInProvider: loggedUser?.signInProvider || null,
      pharmacyList: user?.SENSITIVE_pharmacies as PharmacyInformationInput[],
      ...getMfaParts(
        user?.SENSITIVE_country && user?.SENSITIVE_phone
          ? `+${user?.SENSITIVE_country}${user?.SENSITIVE_phone}`
          : '',
      ),
    } as ProfileData;
  }, [fhirData, loggedUser]);

  const refetch = async () => {
    refetchFHIR();
    refetchProfile();
  };

  return {
    fhirPatientDataLoading,
    profileData,
    fhirUserData,
    setProfileData,
    fhirDataLoading,
    refetch,
  };
};

export default useProfileDataLoader;
